// Calendar grid cells: CalCell (individual day), WeekAvgCell (right column),
// WeekdayAvgRow (bottom same-weekday avg row).

import React, { memo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { band, adhColor } from "../../../../../utils/nutrition-progress";
import { MACRO_COLORS, translateMacroUnit, translateCalendarLegend, translateCalendarGrid } from "./viewTokens";
import { KcalChip, MiniBar } from "./viewAtoms";
import SvgIcon from "../../../../../parts/SvgIcon";

// ISO weekday order (Mon=1..Sun=7) — numeric, locale-independent identity for
// list keys. weekdayLabel() derives the display text from moment's active
// locale (synced to the app language in js/i18n.js) instead of a hardcoded
// English array, so column headers translate automatically.
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const weekdayLabel = (isoWeekday) => moment().isoWeekday(isoWeekday).format("ddd");

// [macro key, translation key] — one structure to keep the mini-bar's three
// macros and their i18n labels in sync instead of two parallel lookups.
const MACRO_MINI = [
  ["proteins", "macroProteinShort"],
  ["carbs", "macroCarbsShort"],
  ["fats", "macroFatShort"],
];

const DUMBBELL_ICON = "/img/dumbbell.svg";
const CAMERA_ICON = "/img/svg/camera.svg";
const DROP_ICON = "/img/new-icons/drop-icon.svg";

const leftOver = (t, value, target) => {
  if (value == null || target == null) return "";
  const d = Math.round(target - value);
  return d >= 0
    ? translateCalendarGrid(t, "remainingLeft", { amount: d })
    : translateCalendarGrid(t, "remainingOver", { amount: Math.abs(d) });
};

/* ---- day cell -------------------------------------------------------- */

export const CalCell = memo(({ day, planStartsHere, isFirstDay, isRowStart, onDayClick }) => {
  const { t } = useTranslation("athlete-portal");

  const dayMom = moment(String(day.utcDate), "YYYYMMDD");
  const dayNum = dayMom.date();
  // Show "MMM D" (not just the bare day number) on the 1st of a month, the
  // first day of the reporting range, or the first in-range cell of a row —
  // so every row self-identifies its month even in a multi-month range with
  // no left-hand week-label column (a row can straddle two months, e.g.
  // "May 26 ... 31 Jun 1" — each half still shows its own month this way).
  const showMonth = dayNum === 1 || isFirstDay || isRowStart;
  const dateLabel = showMonth ? `${dayMom.format("MMM")} ${dayNum}` : String(dayNum);

  // Days outside the reporting range (outOfRange) render exactly like any
  // other day below — logged/unlogged still drives the content. outOfRange
  // only matters to the caller (CalendarGrid), which suppresses report-level
  // markers like the "new plan" pill for them.
  const token = band(day);

  // True exactly when this ONE cell renders the pill + the large kcal chip
  // + the macro bars together — mirrors the JSX conditions below. Flagged
  // here (not detected in CSS) because a :has() can't be nested inside
  // another :has() per spec — CSS only needs this single-level marker to
  // then grow every cell in the row (see np-cal-cell-slot--tall in nutrition.css).
  const isTallCell = planStartsHere && day.logged && day.consumed && day.target;

  return (
    <td className={`np-cal-cell-slot${isTallCell ? " np-cal-cell-slot--tall" : ""}`}>
      {/* type="button" + np-cal-cell-button (nutrition.css) reset the native
          button chrome (border/appearance) — .np-cal-cell's own padding/
          background/height already win by specificity over the UA
          stylesheet, so only border needs resetting explicitly. No nested
          interactive content in here today, so a real <button> is valid
          (unlike Log's day column — see LogGrid.js). */}
      <button
        type="button"
        onClick={() => onDayClick?.(day)}
        aria-label={dayMom.format("MMM D, YYYY")}
        className={`np-cal-cell np-cal-cell-button ${day.logged ? "" : "np-cal-cell--empty"} ${
          day.outOfRange ? "np-cal-cell--out-range" : ""
        }`}
      >
        <div className="np-cal-cell__header">
          <span className="np-cal-cell__date">
            {dateLabel}
          </span>
          <span className="np-cal-cell__badges">
            {day.workout && (
              <SvgIcon
                src={DUMBBELL_ICON}
                useCurrentColor
                classNames="np-cal-cell__badge-icon np-cal-cell__badge-icon--workout"
              />
            )}
            {day.photos > 0 && (
              <span className="np-cal-cell__photo-badge">
                <SvgIcon src={CAMERA_ICON} useCurrentColor classNames="np-cal-cell__badge-icon" />
                {day.photos}
              </span>
            )}
            {day.period && (
              <SvgIcon
                src={DROP_ICON}
                useCurrentColor
                classNames="np-cal-cell__badge-icon np-cal-cell__badge-icon--period"
              />
            )}
          </span>
        </div>

        {/* Plan pill renders AFTER the chip/macros (not before) so every cell
            in the row starts its kcal chip at the same y — a pill inserted
            above it here would push just that one cell's chip down, throwing
            off alignment across the week while the sibling cells' chips stay
            put right under the header. */}
        {day.logged && day.consumed ? (
          <>
            <KcalChip
              kcal={day.consumed.kcal}
              token={token}
              large
              sub={day.target ? leftOver(t, day.consumed.kcal, day.target.kcal) : undefined}
            />
            {day.target && (
              <div className="np-cal-cell__macros">
                {MACRO_MINI.map(([mac, labelKey]) => (
                  <MiniBar
                    key={mac}
                    value={day.consumed[mac]}
                    target={day.target[mac]}
                    color={MACRO_COLORS[mac]}
                    label={translateCalendarGrid(t, labelKey)}
                    unit={translateMacroUnit(t, mac)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="np-cal-cell__empty-state">
            {/* Reuses the legend's "not logged" label verbatim — same text, same meaning. */}
            <span className="np-cal-cell__empty-text">{translateCalendarLegend(t, "notLogged")}</span>
          </div>
        )}

        {planStartsHere && (
          <div className="np-cal-cell__plan-pill-wrap">
            <span className="np-cal-cell__plan-pill">▶ {translateCalendarGrid(t, "newPlan")}</span>
          </div>
        )}
      </button>
    </td>
  );
});

/* ---- week avg cell (right column) ----------------------------------- */

export const WeekAvgCell = ({ avg, spansPlans }) => {
  const { t } = useTranslation("athlete-portal");
  const token   = adhColor(avg.adh);
  const hasData = avg.n > 0 && avg.consumed;

  return (
    <td className="np-cal-cell-slot">
      <div className="np-cal-week-avg">
        <span className="np-cal-week-avg__label">{translateCalendarGrid(t, "weekAvg")}</span>
        {hasData && <KcalChip kcal={avg.consumed.kcal} token={token} />}
        <span
          className={`np-cal-week-avg__meta ${hasData ? "" : "np-cal-week-avg__meta--no-data"}`}
        >
          {translateCalendarGrid(t, "loggedCount", { logged: avg.n, total: avg.total })}
        </span>
        {spansPlans && (
          <span className="np-cal-week-avg__spans">
            {translateCalendarGrid(t, "spansPlans", { count: spansPlans })}
          </span>
        )}
      </div>
    </td>
  );
};

/* ---- weekday avg row (bottom) --------------------------------------- */

// Bare value under a thin adherence-colored bar — deliberately not a KcalChip
// (no border/background): this is a compact summary strip, not another row
// of day cards. token drives both the bar and the value color via CSS
// modifier classes (np-cal-weekday-avg__bar--<token> / __value--<token>).
const WeekdayAvgValue = ({ avg }) => {
  const { t } = useTranslation("athlete-portal");
  const hasData = avg.n > 0 && avg.consumed;
  if (!hasData) {
    return <span className="np-cal-weekday-avg__empty">—</span>;
  }
  const token = adhColor(avg.adh);
  return (
    <>
      <div className={`np-cal-weekday-avg__bar np-cal-weekday-avg__bar--${token}`} />
      <span className={`np-cal-weekday-avg__value np-cal-weekday-avg__value--${token}`}>
        {Math.round(avg.consumed.kcal).toLocaleString()} {translateCalendarGrid(t, "kcalUnit")}
      </span>
    </>
  );
};

WeekdayAvgValue.propTypes = { avg: PropTypes.object.isRequired };

export const WeekdayAvgRow = ({ avgs, overallAvg }) => {
  return (
    <tr className="np-cal-weekday-avg">
      {avgs.map((avg, i) => (
        <td key={ISO_WEEKDAYS[i]} className="np-cal-weekday-avg__cell">
          <WeekdayAvgValue avg={avg} />
        </td>
      ))}
      <td className="np-cal-weekday-avg__cell">
        <WeekdayAvgValue avg={overallAvg} />
      </td>
    </tr>
  );
};

/* ---- prop types ----------------------------------------------------- */

export const dayShape = PropTypes.shape({
  utcDate: PropTypes.number,
  outOfRange: PropTypes.bool,
  logged: PropTypes.bool,
  workout: PropTypes.bool,
  period: PropTypes.bool,
  photos: PropTypes.number,
  target: PropTypes.object,
  consumed: PropTypes.object,
  planId: PropTypes.string,
});

CalCell.propTypes = {
  day: dayShape.isRequired,
  planStartsHere: PropTypes.bool,
  isFirstDay: PropTypes.bool,
  isRowStart: PropTypes.bool,
  onDayClick: PropTypes.func,
};

WeekAvgCell.propTypes = {
  avg: PropTypes.object.isRequired,
  spansPlans: PropTypes.number,
};

WeekdayAvgRow.propTypes = {
  avgs: PropTypes.arrayOf(PropTypes.object).isRequired,
  overallAvg: PropTypes.object.isRequired,
};
